import { useTranslation } from "react-i18next";
import { Dropdown } from "@/components/ui/modal";
import { TimeIcon } from "@/components/agent-dashboard/assets";
import { generateHourlyTimeSlots } from "@/utils/generate-hourly-time-slots";
import { TextInput } from "@/components/common/text-input";
import { PushSettingsData } from "@/manager/manager/agent-dashboard-user-management-manager";
import { cn } from "@/lib/utils";

interface PushSettingsProps {
  currentPushSetting: PushSettingsData;
  frequencyError: string;
  countError: string;
  timeError: string;
  changePushSettings: (key: keyof PushSettingsData, value: number) => void;
}

/** 推送设置 */
export const PushSettings = ({
  currentPushSetting,
  frequencyError,
  countError,
  timeError,
  changePushSettings,
}: PushSettingsProps) => {
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, key: keyof PushSettingsData) => {
    const value = e.target.value.replace(/\D/g, '');
    changePushSettings(key, Number(value));
  };

  return (
    <div className="w-full h-full flex flex-col relative gap-5">
      <div className='flex items-center justify-between text-base font-medium text-[rgba(0,0,0,1)]'>
        <span>{t("agent_dashboard.user_detail.push_settings.title")}</span>
      </div>
      <div className='flex gap-10 flex-wrap select-none flex-shrink-0'>
        <div className='w-[22.4rem]'>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-3 inline-block'>
            {t('agent_dashboard.user_detail.push_settings.push_frequency')}
            <span className='text-[rgba(117,66,247,1)] pl-0.5'>*</span>
          </label>
          <div className='relative flex items-center gap-4'>
            <div className='relative w-[17rem]'>
              <TextInput
                type="text"
                value={currentPushSetting?.pushFrequencyDay || ''}
                placeholder={t('agent_dashboard.user_detail.push_settings.push_frequency_placeholder')}
                onChange={(e) => handleChange(e, 'pushFrequencyDay')}
                error={!!frequencyError}
                helperText={frequencyError}
                maxLength={3}
              />
            </div>
            <span className='flex-shrink-0 text-sm font-medium text-[rgba(0,0,0,1)]'>
              {t('agent_dashboard.user_detail.push_settings.push_frequency_unit')}
            </span>
          </div>
        </div>
        <div className='w-[22.4rem] mr-10'>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-3 inline-block'>
            {t('agent_dashboard.user_detail.push_settings.push_amount')}
            <span className='text-[rgba(117,66,247,1)] pl-0.5'>*</span>
          </label>
          <div className='relative flex items-center gap-4'>
            <div className='relative w-[17rem]'>
              <TextInput
                type="text"
                value={currentPushSetting?.pushCount || ''}
                placeholder={t('agent_dashboard.user_detail.push_settings.push_amount_placeholder')}
                onChange={(e) => handleChange(e, 'pushCount')}
                error={!!countError}
                helperText={countError}
                max={3}
              />
            </div>
            <span className='flex-shrink-0 text-sm font-medium text-[rgba(0,0,0,1)]'>{t('agent_dashboard.user_detail.push_settings.push_amount_unit')}</span>
          </div>
        </div>
        <div className='max-w-max'>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-3 inline-block'>{t('agent_dashboard.user_detail.push_settings.time')}</label>
          <Dropdown
            value={currentPushSetting?.pushTime?.toString()}
            prefixIcon={<TimeIcon className="size-5" />}
            options={generateHourlyTimeSlots()}
            width="23rem"
            placeholder={t('agent_dashboard.user_detail.push_settings.push_time_placeholder')}
            className={cn(
              'h-11',
              timeError && "border-[rgba(217,45,32,1)] hover:border-[rgba(217,45,32,1)] focus:border-[rgba(217,45,32,1)]"
            )}
            onValueChange={(value) => changePushSettings('pushTime', Number(value))}
          />
          {timeError && <div className='text-[rgba(217,45,32,1)] text-xs pl-1 pt-0.5'>{timeError}</div>}
        </div>
      </div>
    </div>
  )
}
